import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment-timezone';
import { fetcher } from '@weezevent/weezjs-core';
import { useInterval } from '../../../components/export/exportButton';
import { useTranslation } from 'react-i18next';
import { ToastContext } from '@weezevent/nacre';
import useAppContext from '../../../context/useAppContext';

const useFetchStatistics = ({ route, onStats = () => {}, onError = () => {} }) => {
    const updateDelay = 30000;
    const noStatsDelay = 5000;
    const [{ organization, event }] = useAppContext();

    const [delay, setDelay] = React.useState(1);
    const [stats, setStats] = React.useState(null);

    const call = () => {
        setDelay(updateDelay);
        const statsPromise = fetcher.get(route, {
            query: {
                stats: ['attendees_stats', 'orders_stats', 'revenues_stats', 'payments_stats'].join(',')
            },
            cache: false
        });

        const inProgressPromise = fetcher.get(`/ticket/organizations/${organization.pk}/events/${event.pk}/cart`, {
            cache: false
        });

        Promise.all([statsPromise, inProgressPromise])
            .then(
                ([
                    { response: stats },
                    {
                        response: { total }
                    }
                ]) => {
                    if (stats.some(s => s.last_computed === null)) {
                        // If any stats are null, set delay to minimum value
                        return setDelay(noStatsDelay);
                    }

                    // Add inprogress to attendee stats
                    const attendee_stat = stats.find(s => s.name === 'attendees_stats');
                    attendee_stat.value = { ...attendee_stat.value, attendees_in_progress: total };

                    // Set total
                    setStats(stats);
                    onStats(stats);
                }
            )
            .catch(e => {
                if (e.code === 403) {
                    setDelay(null);
                } else {
                    onError();
                }
            });
    };

    useInterval(call, delay);

    return stats;
};

export const useFetchGraphStatistics = ({ route, queryParams = {}, onStats = () => {}, onError = () => {}, disabled = false }) => {
    const updateDelay = 60000;
    const noStatsDelay = 5000;
    const [{ event }] = useAppContext();

    const now = moment.utc().tz(event.timezone);

    const [delay, setDelay] = React.useState(1);
    const [stats, setStats] = React.useState(null);

    const call = React.useCallback(() => {
        setDelay(updateDelay);
        fetcher
            .get(route, {
                query: queryParams,
                cache: false
            })
            .then(({ response: stats }) => {
                if (stats.some(s => s.last_computed === null || now.diff(moment(s.last_computed), 'hours') > 24)) {
                    // If any stats are null, set delay to minimum value
                    setDelay(noStatsDelay);
                } else {
                    setDelay(null);
                }

                setStats(stats);
                onStats(stats);
            })
            .catch(e => {
                if (e.code === 403) {
                    setDelay(null);
                } else {
                    onError();
                }
            });
    }, [route, queryParams, onStats, onError]);

    if (!disabled) {
        useInterval(call, delay);
    }

    return stats;
};

export default function StatisticsFetcher({ children = null, route, onStats = () => {} }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const statistics = useFetchStatistics({
        route,
        onStats,
        onError: () => toast.error(t('ticket.eventDashboard.statistics.error_retrieving_stats'))
    });

    return React.Children.map(children, (child, index) => {
        return React.cloneElement(child, {
            ...child.props,
            data: statistics
        });
    });
}

StatisticsFetcher.propTypes = {
    children: PropTypes.node,
    route: PropTypes.string.isRequired
};
